<script setup lang="ts">
import { inject, computed } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import type { User } from 'src/modules/User/models/User';
import type { UserRole } from 'src/types/User';
import { mdiHandWaveOutline } from '@quasar/extras/mdi-v7';

const props = defineProps<{
  streak?: number;
  longestStreak?: number;
}>();

const $q = useQuasar();
const { t } = useI18n();
const currentUser = inject('currentUser') as Ref<User | null>;
const currentUserRole = inject('currentUserRole') as Ref<UserRole | null>;

const bannerToneClass = computed(() => {
  if (currentUserRole.value === 'parent') return 'banner-parent text-white';
  // Switch the gradient class (and text) by theme, rather than relying on a
  // CSS override that ties on specificity with the scoped base class.
  const dark = $q.dark.isActive;
  if (currentUserRole.value === 'child') {
    return dark ? 'banner-child-dark text-white' : 'banner-child text-dark';
  }
  return dark ? 'banner-staff-dark text-white' : 'banner-staff text-dark';
});

const subtitleClass = computed(() => {
  if (currentUserRole.value === 'parent' || $q.dark.isActive)
    return 'text-body2 q-mt-xs text-grey-3';
  return 'text-body2 q-mt-xs text-grey-8';
});

// Role-specific welcome line (the previous default showed the parent's
// "choose a child" copy to teachers and students too).
const subtitle = computed(() => {
  switch (currentUserRole.value) {
    case 'parent':
      return t('WELCOME_BACK_MESSAGE_PARENT');
    case 'teacher':
      return t('WELCOME_BACK_MESSAGE_TEACHER');
    case 'student':
      return t('WELCOME_BACK_MESSAGE_STUDENT');
    case 'child':
    default:
      return t('WELCOME_BACK_MESSAGE');
  }
});

const streakValue = computed(() => Math.max(props.streak || 0, 0));
const longestStreakValue = computed(() =>
  Math.max(props.longestStreak || 0, 0),
);
const showStreakChip = computed(() => {
  return streakValue.value > 0 || longestStreakValue.value > 0;
});

const streakChipClass = computed(() => {
  if (currentUserRole.value === 'parent') {
    return 'bg-white text-brand text-weight-medium';
  }
  return 'bg-primary text-dark text-weight-medium';
});

const actionButtonColor = computed(() => {
  return currentUserRole.value === 'parent' ? 'white' : 'primary';
});

const actionButtonClass = computed(() => {
  return currentUserRole.value === 'parent' ? '' : 'bg-white';
});

// Students have no dedicated destination from here (no class pages, no browse);
// they work from Courses / Sections, so the greeting shows no action button.
const showAction = computed(() => currentUserRole.value !== 'student');

const actionLabel = computed(() => {
  if (currentUserRole.value === 'child') return t('GO_TO_COURSE_STORE');
  if (currentUserRole.value === 'teacher') return t('GO_TO_CLASSES');
  return t('GO_TO_CHILDREN');
});

const actionRouteName = computed(() => {
  if (currentUserRole.value === 'child') return 'courses';
  if (currentUserRole.value === 'teacher') return 'school.teacher.home';
  return 'children.manage';
});

const userName = computed(() => {
  // firstname is common identity on the user now.
  return currentUser.value?.firstname || '__';
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 4) return t('GOOD_NIGHT');
  if (hour < 12) return t('GOOD_MORNING');
  if (hour < 18) return t('GOOD_AFTERNOON');
  return t('GOOD_EVENING');
});
</script>

<template>
  <q-banner
    v-if="userName"
    rounded
    :class="bannerToneClass"
    class="q-my-lg shadow-2"
  >
    <template #avatar>
      <q-icon
        :name="mdiHandWaveOutline"
        size="lg"
      />
    </template>
    <div class="text-h5 text-weight-medium">
      {{ greeting }}, {{ userName }}!
    </div>
    <div :class="subtitleClass">
      {{ subtitle }}
    </div>

    <template #action>
      <div class="row items-center q-gutter-sm no-wrap">
        <q-chip
          v-if="showStreakChip"
          dense
          :class="streakChipClass"
          icon="mdi-fire"
        >
          {{ $t('CURRENT_STREAK') }}: {{ streakValue }}
          <span
            v-if="longestStreakValue > 0"
            class="q-ml-xs"
          >
            ({{ $t('LONGEST_STREAK') }}: {{ longestStreakValue }})
          </span>
        </q-chip>

        <q-btn
          v-if="showAction"
          flat
          dense
          rounded
          :label="actionLabel"
          :class="actionButtonClass"
          :color="actionButtonColor"
          :to="{
            name: actionRouteName,
          }"
        />
      </div>
    </template>
  </q-banner>
</template>

<style scoped>
.banner-parent {
  background: linear-gradient(to right, var(--q-primary), var(--q-secondary));
}

.banner-child {
  background: linear-gradient(to right, var(--q-accent), var(--q-primary));
}

.banner-staff {
  background: linear-gradient(to right, #fff3cf, #e7f0ff);
}

.banner-child-dark {
  background: linear-gradient(to right, #4a25ba, #8120b3);
}

.banner-staff-dark {
  background: linear-gradient(to right, #2a2340, #1f2740);
}
</style>
